import { useRef, useState, useCallback, useEffect } from 'react';

// Both gates must pass since the last *written* point before a new one is
// accepted. At driving speed the distance gate clears almost immediately, so
// the time gate becomes the effective cap (~7.5 writes/min/participant);
// while stationary, nothing is written until real movement happens.
const MIN_INTERVAL_MS = 8000;
const MIN_DISTANCE_M = 15;
// Drop fixes worse than this (cold GPS / IP fallback) so a bad reading can't
// spike the drawn trail across the map.
const MAX_ACCURACY_M = 75;

export function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

// Owns the watchPosition/clearWatch lifecycle and the write-throttle for the
// Area Search live trail. Pure geo/throttle logic — knows nothing about
// Firestore; the caller supplies onTrailPoint to persist accepted fixes.
// Sharing never starts on its own (no auto-start): start() must be called
// from an explicit user action.
//
// `options.minDistanceM`/`options.maxAccuracyM` let a caller override the
// module defaults per-search (e.g. grid search mode ties these to the chosen
// cell size / relaxes the accuracy cutoff for under-canopy GPS) without
// changing behavior for callers that don't pass them.
export function useLocationTracking(onTrailPoint, options = {}) {
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);
  const [here, setHere] = useState(null); // last accepted {lat,lng} — local "you are here"
  const watchIdRef = useRef(null);
  const hereRef = useRef(null);
  const lastWrittenRef = useRef(null); // { pos:{lat,lng}, at:ms }
  // Refs (not plain consts) so `start`'s watchPosition callback — memoized
  // once via useCallback — always reads the latest override even if the
  // caller's options change after mount (e.g. grid config loading in async).
  const minDistanceMRef = useRef(options.minDistanceM ?? MIN_DISTANCE_M);
  minDistanceMRef.current = options.minDistanceM ?? MIN_DISTANCE_M;
  const maxAccuracyMRef = useRef(options.maxAccuracyM ?? MAX_ACCURACY_M);
  maxAccuracyMRef.current = options.maxAccuracyM ?? MAX_ACCURACY_M;
  const onTrailPointRef = useRef(onTrailPoint);
  onTrailPointRef.current = onTrailPoint;

  const stop = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setSharing(false);
    // Force one final write so the trail's last segment reaches the current
    // position, even if it hadn't yet cleared the throttle gates.
    const current = hereRef.current;
    const last = lastWrittenRef.current;
    if (current && (!last || current.lat !== last.pos.lat || current.lng !== last.pos.lng)) {
      onTrailPointRef.current && onTrailPointRef.current(current);
    }
  }, []);

  const start = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Location is not available in this browser.');
      return;
    }
    setError(null);
    lastWrittenRef.current = null;
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        if (accuracy != null && accuracy > maxAccuracyMRef.current) return;
        const point = { lat, lng };
        hereRef.current = point;
        setHere(point);

        const now = pos.timestamp || Date.now();
        const last = lastWrittenRef.current;
        const shouldWrite = !last
          || (now - last.at >= MIN_INTERVAL_MS && haversineMeters(last.pos, point) >= minDistanceMRef.current);
        if (shouldWrite) {
          lastWrittenRef.current = { pos: point, at: now };
          onTrailPointRef.current && onTrailPointRef.current(point);
        }
      },
      err => setError(err.message || 'Could not get your location.'),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    setSharing(true);
  }, []);

  // Safety net: clear the watch on unmount (route navigation away from the
  // board) even if the user never clicked Stop.
  useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
  }, []);

  return { sharing, here, error, start, stop };
}
